import type { LocalRecord } from '@dovefiniscono/sync';

export type Transaction = LocalRecord<'transactions'>;
export type MoneyAccount = LocalRecord<'moneyAccounts'>;
export type Category = LocalRecord<'categories'>;
export type Budget = LocalRecord<'budgets'>;
export type SavingsGoal = LocalRecord<'savingsGoals'>;

/** Categories go three levels deep: Cena › Cena fuori › Sushi. */
export const MAX_CATEGORY_DEPTH = 3;

/** What the money figures need to know about accounts and categories. */
export type MoneyContext = {
  /** Accounts that are not yours ("pagato da altri"): a cost, never a balance. */
  external: ReadonlySet<string>;
  /** Subcategory → the category right above it. */
  parentOf: ReadonlyMap<string, string>;
  /** Any category below the top → its top-level category. */
  rootOf: ReadonlyMap<string, string>;
  /** Business categories, those below a business category included. */
  business: ReadonlySet<string>;
};

export const NO_CONTEXT: MoneyContext = {
  external: new Set(),
  parentOf: new Map(),
  rootOf: new Map(),
  business: new Set(),
};

/** The categories above one, nearest first (never looping, never deeper than allowed). */
export function ancestorsOf(id: string, parentOf: ReadonlyMap<string, string>): string[] {
  const above: string[] = [];
  let current = parentOf.get(id);
  while (current && !above.includes(current) && current !== id && above.length < 5) {
    above.push(current);
    current = parentOf.get(current);
  }
  return above;
}

/** The category is `ancestor` itself or anywhere below it: "Cena" takes "Cena fuori › Sushi". */
export const isWithin = (
  categoryId: string,
  ancestor: string,
  parentOf: ReadonlyMap<string, string>,
) => categoryId === ancestor || ancestorsOf(categoryId, parentOf).includes(ancestor);

/** The level of a category: 1 at the top, 3 at the deepest. */
export const depthOf = (id: string, parentOf: ReadonlyMap<string, string>) =>
  ancestorsOf(id, parentOf).length + 1;

export function moneyContext(
  accounts: readonly MoneyAccount[],
  categories: readonly Category[],
): MoneyContext {
  const ids = new Set(categories.map((category) => category.id));
  const parentOf = new Map<string, string>();
  for (const category of categories) {
    if (category.parentId && category.parentId !== category.id && ids.has(category.parentId)) {
      parentOf.set(category.id, category.parentId);
    }
  }
  const rootOf = new Map<string, string>();
  for (const id of parentOf.keys()) {
    const above = ancestorsOf(id, parentOf);
    const root = above[above.length - 1];
    if (root) rootOf.set(id, root);
  }
  const flagged = new Set(categories.filter((c) => c.business).map((c) => c.id));
  const business = new Set(
    categories
      .filter((c) => [c.id, ...ancestorsOf(c.id, parentOf)].some((id) => flagged.has(id)))
      .map((c) => c.id),
  );
  return {
    external: new Set(accounts.filter((a) => a.kind === 'external').map((a) => a.id)),
    parentOf,
    rootOf,
    business,
  };
}

const inMonth = (transaction: Transaction, month: string) =>
  transaction.occurredOn.startsWith(month);

/** Opening balance plus every movement, per account. */
export function accountBalances(
  accounts: readonly MoneyAccount[],
  transactions: readonly Transaction[],
): Map<string, number> {
  const balances = new Map(accounts.map((account) => [account.id, account.openingBalance]));
  for (const transaction of transactions) {
    const current = balances.get(transaction.accountId);
    if (current !== undefined) balances.set(transaction.accountId, current + transaction.amount);
  }
  return balances;
}

export type PeriodTotals = {
  /** Money in, on your accounts. */
  income: number;
  /** Money out of your accounts. */
  expenses: number;
  /** Spent for you by others (parents, a shared fund). */
  paidByOthers: number;
  /** Business spending, whoever paid it. */
  business: number;
  /** What the period cost: your expenses plus what others paid. */
  cost: number;
  /** Income minus your expenses. */
  net: number;
};

function totalsOf(
  transactions: readonly Transaction[],
  include: (transaction: Transaction) => boolean,
  context: MoneyContext,
): PeriodTotals {
  let income = 0;
  let expenses = 0;
  let paidByOthers = 0;
  let business = 0;
  for (const transaction of transactions) {
    if (!include(transaction)) continue;
    const external = context.external.has(transaction.accountId);
    if (transaction.amount > 0) {
      if (!external) income += transaction.amount;
      continue;
    }
    if (external) paidByOthers -= transaction.amount;
    else expenses -= transaction.amount;
    if (transaction.categoryId && context.business.has(transaction.categoryId)) {
      business -= transaction.amount;
    }
  }
  return {
    income,
    expenses,
    paidByOthers,
    business,
    cost: expenses + paidByOthers,
    net: income - expenses,
  };
}

export function monthTotals(
  transactions: readonly Transaction[],
  month: string,
  context: MoneyContext = NO_CONTEXT,
): PeriodTotals {
  return totalsOf(transactions, (t) => inMonth(t, month), context);
}

export type MethodTotals = { cash: number; card: number; transfer: number; unknown: number };

/** Money out of the month by how it was paid, whoever's money it was. */
export function methodTotals(transactions: readonly Transaction[], month: string): MethodTotals {
  const totals: MethodTotals = { cash: 0, card: 0, transfer: 0, unknown: 0 };
  for (const transaction of transactions) {
    if (transaction.amount >= 0 || !inMonth(transaction, month)) continue;
    totals[transaction.method ?? 'unknown'] -= transaction.amount;
  }
  return totals;
}

export type YearMonth = PeriodTotals & { month: string };

/** The twelve months of a year, then the year's totals and the average monthly cost. */
export function yearByMonth(
  transactions: readonly Transaction[],
  year: number,
  context: MoneyContext = NO_CONTEXT,
  todayISO?: string,
) {
  const months: YearMonth[] = Array.from({ length: 12 }, (_, index) => {
    const month = `${year}-${String(index + 1).padStart(2, '0')}`;
    return { month, ...monthTotals(transactions, month, context) };
  });
  const total = totalsOf(transactions, (t) => t.occurredOn.startsWith(`${year}-`), context);
  // Average over the months that have started (the whole year once it is over), never over zero.
  const current = todayISO?.slice(0, 7);
  const started = current ? months.filter((m) => m.month <= current).length : 12;
  return { months, total, averageCost: Math.round(total.cost / Math.max(1, started)) };
}

export type CategorySpend = {
  categoryId: string | null;
  name: string;
  cents: number;
  share: number;
  /** Subcategories, largest first; shares are of this category. */
  children: { categoryId: string | null; name: string; cents: number; share: number }[];
};

/**
 * "Dove finiscono": money out of the month by category, largest first. Subcategories add up into
 * their category; what others paid for you counts too, because it is still what you cost.
 */
export function spendingByCategory(
  transactions: readonly Transaction[],
  categories: readonly Category[],
  month: string,
  context: MoneyContext = NO_CONTEXT,
): CategorySpend[] {
  const names = new Map(categories.map((category) => [category.id, category.name]));
  const roots = new Map<string | null, { cents: number; leaves: Map<string | null, number> }>();
  let total = 0;
  for (const transaction of transactions) {
    if (transaction.amount >= 0 || !inMonth(transaction, month)) continue;
    const leaf =
      transaction.categoryId && names.has(transaction.categoryId) ? transaction.categoryId : null;
    const root = leaf ? (context.rootOf.get(leaf) ?? leaf) : null;
    // A third-level category adds up into its second level: Sushi into Cena fuori.
    const second =
      leaf && root && leaf !== root
        ? ([leaf, ...ancestorsOf(leaf, context.parentOf)].find(
            (id) => context.parentOf.get(id) === root,
          ) ?? leaf)
        : leaf;
    const entry = roots.get(root) ?? { cents: 0, leaves: new Map() };
    entry.cents -= transaction.amount;
    entry.leaves.set(second, (entry.leaves.get(second) ?? 0) - transaction.amount);
    roots.set(root, entry);
    total -= transaction.amount;
  }
  return [...roots]
    .map(([categoryId, { cents, leaves }]) => {
      const hasSubcategories = [...leaves.keys()].some((leaf) => leaf !== categoryId);
      return {
        categoryId,
        name: categoryId ? (names.get(categoryId) ?? 'Senza categoria') : 'Senza categoria',
        cents,
        share: total > 0 ? cents / total : 0,
        children: hasSubcategories
          ? [...leaves]
              .map(([leaf, value]) => ({
                categoryId: leaf,
                name:
                  leaf === categoryId || !leaf
                    ? 'Senza sottocategoria'
                    : (names.get(leaf) ?? 'Senza sottocategoria'),
                cents: value,
                share: cents > 0 ? value / cents : 0,
              }))
              .sort((a, b) => b.cents - a.cents)
          : [],
      };
    })
    .sort((a, b) => b.cents - a.cents);
}

export type BudgetStatus = { budget: Budget; spent: number; remaining: number; ratio: number };

/** A budget on a category also counts everything below it. */
export function budgetStatuses(
  budgets: readonly Budget[],
  transactions: readonly Transaction[],
  month: string,
  context: MoneyContext = NO_CONTEXT,
): BudgetStatus[] {
  return budgets.map((budget) => {
    const spent = transactions
      .filter(
        (t) =>
          t.amount < 0 &&
          inMonth(t, month) &&
          t.categoryId !== null &&
          isWithin(t.categoryId, budget.categoryId, context.parentOf),
      )
      .reduce((sum, t) => sum - t.amount, 0);
    return {
      budget,
      spent,
      remaining: budget.monthlyLimit - spent,
      ratio: spent / budget.monthlyLimit,
    };
  });
}

/** Movements of a month grouped by day, newest day first. */
export function groupByDay(transactions: readonly Transaction[], month: string) {
  const days = new Map<string, Transaction[]>();
  for (const transaction of transactions) {
    if (!inMonth(transaction, month)) continue;
    const list = days.get(transaction.occurredOn) ?? [];
    list.push(transaction);
    days.set(transaction.occurredOn, list);
  }
  return [...days]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([day, items]) => ({
      day,
      // Latest first: by time of day, movements without one last.
      items: items.sort(
        (a, b) =>
          (b.time ?? '').localeCompare(a.time ?? '') || b.createdAt.localeCompare(a.createdAt),
      ),
      net: items.reduce((sum, t) => sum + t.amount, 0),
    }));
}
